import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

const STORAGE_PREFIX = "ui_url_obj:";

// 같은 키라도 화면(경로)마다 저장 공간을 분리해 값이 섞이지 않게 한다.
function buildStorageKey(key: string): string {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return `${STORAGE_PREFIX}${path}:${key}`;
}

function readStorage(storageKey: string): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") return parsed as Record<string, string>;
    return {};
  } catch {
    return {};
  }
}

function writeStorage(storageKey: string, value: Record<string, string>): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // 저장 실패(용량 초과 등)는 무시
  }
}

function shallowEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => a[k] === b[k]);
}

/**
 * URL 쿼리 파라미터와 sessionStorage를 함께 사용해 "여러 필드"의 화면 상태를 유지하는 훅.
 *
 * - 상태가 바뀌면 주소창(URL)의 쿼리 파라미터를 갱신 → 새로고침·링크 공유에도 유지
 * - URL에 값이 없으면 sessionStorage에서 복원 (탭 복귀/리다이렉트 대응)
 * - 기본값으로 되돌아가면 URL에서 파라미터를 제거해 주소를 깔끔하게 유지
 */
export default function useUrlStateObject<T extends Record<string, string>>(
  key: string,
  defaults: T
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const storageKey = buildStorageKey(key);
  const defaultsRef = useRef<T>(defaults);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const searchKey = searchParams.toString();

  const resolve = useCallback(
    (sp: URLSearchParams): T => {
      const base = defaultsRef.current;
      const stored = readStorage(storageKey);
      const result: Record<string, string> = { ...base };
      for (const field of Object.keys(base)) {
        const fromUrl = sp.get(field);
        if (fromUrl !== null && fromUrl !== "") {
          result[field] = fromUrl;
        } else if (fromUrl === null && stored[field] !== undefined) {
          result[field] = stored[field];
        }
      }
      return result as T;
    },
    [storageKey]
  );

  const [value, setValue] = useState<T>(() => resolve(searchParams));

  // 최초 마운트 시, 복원된 값이 기본값과 다르면 주소에 반영
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    const current = value;
    const base = defaultsRef.current;
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        let changed = false;
        for (const field of Object.keys(base)) {
          if (sp.get(field) === null && current[field] !== base[field]) {
            sp.set(field, current[field]);
            changed = true;
          }
        }
        return changed ? sp : prev;
      },
      { replace: true }
    );
    // 최초 1회만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로/앞으로 가기 등으로 URL이 바뀌면 상태를 동기화
  useEffect(() => {
    const next = resolve(searchParamsRef.current);
    setValue((prev) => (shallowEqual(prev, next) ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);

  const update = useCallback(
    (next: T) => {
      setValue(next);
      writeStorage(storageKey, next);
      const base = defaultsRef.current;
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          for (const field of Object.keys(base)) {
            const v = next[field];
            if (v === base[field]) {
              sp.delete(field);
            } else {
              sp.set(field, v);
            }
          }
          return sp;
        },
        { replace: true }
      );
    },
    [storageKey, setSearchParams]
  );

  return [value, update];
}